import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Briefcase, Gem, Handshake, Home, Plus, Receipt, TrendingUp, User, X, ArrowLeftRight } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";

const tabs = [
  { to: "/", label: "Home", icon: Home, id: "home" },
  { to: "/holdings", label: "Holdings", icon: Briefcase, id: "holdings" },
  { add: true, id: "add" },
  { to: "/ledger", label: "Ledger", icon: Receipt, id: "ledger" },
  { to: "/profile", label: "Profile", icon: User, id: "profile" },
];

const addOptions = [
  { to: "/add/stock", label: "Stock", desc: "Listed equity purchase", icon: TrendingUp, id: "stock" },
  { to: "/add/commodity", label: "Commodity", desc: "Gold, silver, copper & more", icon: Gem, id: "commodity" },
  { to: "/add/business", label: "Business Partnership", desc: "Firm, LLP or company interest", icon: Handshake, id: "business" },
  { to: "/add/settlement", label: "Settlement", desc: "Money paid between people", icon: ArrowLeftRight, id: "settlement" },
];

function AddSheet({ open, onOpenChange }) {
  const nav = useNavigate();
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-w-[480px] mx-auto bg-[#F5F0E9] border-0 rounded-t-3xl" data-testid="add-sheet">
        <div className="px-5 pb-8 pt-2">
          <div className="flex items-center justify-between mb-4">
            <DrawerTitle className="text-lg font-bold">What would you like to add?</DrawerTitle>
            <button onClick={() => onOpenChange(false)} data-testid="add-sheet-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {addOptions.map((o) => (
              <button
                key={o.id}
                data-testid={`add-option-${o.id}`}
                onClick={() => { onOpenChange(false); nav(o.to); }}
                className="hd-card text-left hover:border-[#C8553D]/50 transition-colors active:scale-[0.98]"
              >
                <div className="w-11 h-11 rounded-xl bg-[#F9ECE8] text-[#C8553D] flex items-center justify-center mb-3">
                  <o.icon size={20} />
                </div>
                <div className="font-bold text-[15px]">{o.label}</div>
                <div className="text-xs text-[#718096] mt-0.5">{o.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#EDE6DC]">
      <div className="max-w-[480px] mx-auto min-h-screen bg-[#F5F0E9] relative pb-28 sm:border-x sm:border-[#E2D9CF]">
        <main className="px-4">
          <Outlet />
        </main>
        <nav className="fixed bottom-0 inset-x-0 z-40">
          <div className="max-w-[480px] mx-auto bg-white/95 backdrop-blur-md border-t border-[#E2D9CF] px-2 pt-1.5 pb-safe flex justify-around items-end">
            {tabs.map((t) =>
              t.add ? (
                <button
                  key="add"
                  data-testid="nav-add-button"
                  onClick={() => setOpen(true)}
                  className="-mt-6 w-14 h-14 rounded-full bg-[#C8553D] text-white shadow-lg shadow-[#C8553D]/30 flex items-center justify-center active:scale-95 transition-transform"
                  aria-label="Add"
                >
                  <Plus size={26} />
                </button>
              ) : (
                <NavLink
                  key={t.id}
                  to={t.to}
                  end={t.to === "/"}
                  data-testid={`nav-${t.id}`}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-0.5 w-16 py-1 text-[11px] font-semibold transition-colors ${isActive ? "text-[#C8553D]" : "text-[#718096]"}`
                  }
                >
                  <t.icon size={21} />
                  {t.label}
                </NavLink>
              )
            )}
          </div>
        </nav>
        <AddSheet open={open} onOpenChange={setOpen} />
      </div>
    </div>
  );
}
